<template>
  <div v-if="shouldDisplayGallery" :class="CAROUSEL_MAX_HEIGHT_CLASS">
    <Carousel
      data-test="carousel"
      :items="carouselImages"
      primary-image-wrapper-classes="pl-24"
      :secondary-list-classes="CAROUSEL_MAX_HEIGHT_CLASS"
      :tracking="{ name: 'product', zone: 'pp_carousel' }"
      @scroll-end="onScrollEnd"
      @scroll-start="onScrollStart"
    >
      <template #first="{ items }">
        <MainImageContainer
          ref="mainImageContainer"
          :active-index="currentActiveIndex || 0"
          :allow-media-viewer
          :images="items"
          :is-mobile
          :review="currentReview"
          :with-sticker="shouldDisplaySticker"
          @click="openMediaViewer"
          @extra-item-click="trackExtraItemClick"
          @visible="(index, type) => onVisibleImage(index, type)"
        />
      </template>
      <template #rest="{ item: image, index }">
        <AltImageItem
          :key="image.id || image.src"
          :allow-media-viewer
          class="md:[order:var(--md-order)]"
          :class="{
            // hide on mobile the extra items
            'hidden md:block': image.isExtra || index === 0,
            'relative after:bg-overlay-low after:rounded-lg after:pointer-events-none after:absolute after:inset-0 after:z-[1] after:h-full after:w-full':
              image.type === 'video',
          }"
          :extra-items-indexes
          :image
          :index
          :is-mobile
          :style="{
            '--md-order': image.desktopOrder || 'initial',
          }"
          :total-length="carouselImages.length"
          :with-sticker="index === 0 && shouldDisplaySticker"
          v-bind="getAltImageEvents(image, index)"
        >
          <IconMediaPlay
            v-if="image.isExtra && image.type === 'video'"
            class="absolute bottom-8 left-8 z-[2] fill-[#FFF]"
            size="48"
          />
        </AltImageItem>
        <ExtraItem
          v-if="image.isExtra && image.type === 'review'"
          :allow-media-viewer="false"
          :class="{
            // show on mobile
            'block md:hidden': image.isExtra,
          }"
          :index
          :is-mobile
          :item-size-class="BIG_ITEM_SIZE_CLASS"
        >
          <!-- Keep a stable element in the slot to avoid SSR/CSR shape mismatches -->
          <div :class="BIG_ITEM_SIZE_CLASS">
            <Review
              v-if="currentReview"
              ref="reviewComponent"
              :card-class-name="BIG_ITEM_SIZE_CLASS"
              class="h-full w-full"
              defer
              :review="currentReview"
              @visible="onVisibleImage(index, 'review')"
            />
          </div>
        </ExtraItem>
        <ExtraItem
          v-if="image.isExtra && image.type === 'video'"
          :allow-media-viewer="false"
          :class="{
            // show on mobile
            'block md:hidden': image.isExtra,
          }"
          :index
          :is-mobile
          :item-size-class="BIG_ITEM_SIZE_CLASS"
          static
        >
          <YoutubeVideo
            :class="BIG_ITEM_SIZE_CLASS"
            :img-class-name="BIG_ITEM_SIZE_CLASS"
            :is-mobile
            @start="onVisibleImage(index, 'video')"
          />
        </ExtraItem>
      </template>
    </Carousel>
    <RevMediaViewer
      v-if="allowMediaViewer"
      :alternative-button-label="i18n(translations.alternativeController)"
      :alternative-next="i18n(translations.alternativeNext)"
      :alternative-previous="i18n(translations.alternativePrevious)"
      :close-button-label="i18n(translations.alternativeClose)"
      :current-index="mediaViewerIndex || 0"
      current-index-id="gallery-media-viewer"
      data-test="product-page-media-viewer"
      :name="MODAL_NAMES.MEDIA_VIEWER"
      :thumbnails
      @close="onMediaViewerClose"
    >
      <template #default="{ imgClassNames = '' } = {}">
        <RevIllustration
          v-for="image in baseCarouselImages"
          :key="image.alt"
          :alt="image.alt"
          :class="imgClassNames"
          decoding="async"
          :height="1200"
          loading="lazy"
          :src="image.src"
          :width="1200"
        />
      </template>
    </RevMediaViewer>
  </div>
</template>

<script lang="ts" setup>
import { useHead } from '#imports'
import {
  computed,
  onUnmounted,
  ref,
  shallowRef,
  useTemplateRef,
  watch,
} from 'vue'

import type { GetProductResponse } from '@backmarket/http-api/src/api-specs-navigation-experience/product/product'
import { useExperiments } from '@backmarket/nuxt-module-experiments/useExperiments'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { useClientOnlyComputedBreakpoint } from '@backmarket/utils/composables/useClientOnlyComputedBreakpoint'
import { Breakpoint } from '@backmarket/utils/dom/getCurrentBreakpoint'
import { RevIllustration } from '@ds/components/Illustration'
import { RevMediaViewer } from '@ds/components/MediaViewer'
import { openModal } from '@ds/components/ModalBase'
import { IconMediaPlay } from '@ds/icons/IconMediaPlay'
import { useIllustrationOptimizer } from '@ds/plugin'
import { css } from '@ds/utils/css'

import Carousel from '~/scopes/product/components/Carousel/Carousel.vue'
import { MODAL_NAMES } from '~/scopes/product/constants'

import {
  BIG_ITEM_SIZE_CLASS,
  CAROUSEL_MAX_HEIGHT_CLASS,
  MAIN_IMG_SIZES,
} from './GalleryNew.constants'
import translations from './GalleryNew.translations'
import type { GalleryImage } from './GalleryNew.types'
import AltImageItem from './components/AltImageItem.vue'
import ExtraItem from './components/ExtraItem.vue'
import MainImageContainer from './components/MainImageContainer.vue'
import Review from './components/Review/Review.vue'
import YoutubeVideo from './components/YoutubeVideo/YoutubeVideo.vue'
import { useExtraItems } from './composables/useExtraItems'

const {
  images,
  categoryId,
  productBrand,
  productModel,
  trackingProductModel,
  allowMediaViewer = true,
  allowPartnersImages = true,
  shouldPreloadFirstImg = false,
} = defineProps<{
  images: GetProductResponse['images']
  allowMediaViewer?: boolean
  allowPartnersImages?: boolean
  trackingProductModel: string
  shouldPreloadFirstImg?: boolean
  categoryId: string
  productBrand: string
  productModel: string
}>()

const i18n = useI18n()
const { trackClick, trackCarouselImpression } = useTracking()
const experiments = useExperiments()

const shouldDisplaySticker =
  experiments['experiment.stickerInCarousel'] === 'withSticker'

// Define the ref first so we can use it in defineExpose
const mainImageContainer = useTemplateRef('mainImageContainer')

const shouldDisplayGallery = computed(() => images.length > 0)

const baseCarouselImages = ref(
  images
    .filter((image) => allowPartnersImages || image.source !== 'PARTNER')
    .map((image, index) => ({
      id: `base-${index}-${image.url}`,
      src: image.url,
      alt: image.description,
    })),
)

const {
  currentReview,
  imagesListWithExtraItems,
  extraItemsIndexes,
  areThereAnyVideos,
  areThereAnyReviews,
} = useExtraItems({
  categoryId,
  productBrand,
  productModel,
  baseCarouselImages,
})

const carouselImages = computed(() => {
  // we inject the extra items only if needed,
  // otherwise we return the base carousel images inside
  // the `injectExtraItemsAtPosition` function
  return imagesListWithExtraItems.value
})

const thumbnails = computed(() => {
  return baseCarouselImages.value.map((image) => ({
    ...image,
    width: 100,
    height: 100,
  }))
})

const optimizedImage = useIllustrationOptimizer(
  () => baseCarouselImages.value?.[0]?.src,
)

useHead({
  style: [
    {
      textContent: css`
        :root {
          --carousel-max-size: 45lvh;

          --carousel-big-item-size: calc(80vw - 26px);
          --carousel-big-item-width: var(--carousel-big-item-size);
          --carousel-big-item-max-size: var(--carousel-max-size);

          --gap-between-images: 12px;
          /* Small items are only relevant on mobile */
          --carousel-small-item-size: calc(
            (var(--carousel-big-item-size) / 2) - var(--gap-between-images)
          );
          --carousel-small-item-max-size: calc(
            (var(--carousel-max-size) / 2) - var(--gap-between-images)
          );

          @media (min-width: 768px) {
            /* used both in the main image and the main image */
            --carousel-big-item-width: calc(80vw - 26px);
            /* fit in content or span to '--carousel-big-item-width' if possible */
            --carousel-big-item-size: 100%;
            --carousel-max-size: 448px;

            --carousel-small-item-size: 104px;
          }

          @media (min-width: 1200px) {
            --carousel-big-item-size: 448px;
          }
        }
      `,
    },
  ],
  link: shouldPreloadFirstImg
    ? [
        {
          rel: 'preload',
          href: optimizedImage.value.src,
          as: 'image',
          imagesrcset: optimizedImage.value.srcset,
          imagesizes: MAIN_IMG_SIZES,
        },
      ]
    : [],
})

// ================================================
// Tracking
// ================================================

const breakpoint = useClientOnlyComputedBreakpoint()

const isMobile = computed(
  () =>
    breakpoint.value === Breakpoint.XS || breakpoint.value === Breakpoint.SM,
)

function getTrackingIndex(image: GalleryImage, indexInList: number): number {
  let trackingIndex = indexInList
  if (!isMobile.value) {
    if (image.type === 'video') {
      trackingIndex = 1
    } else if (image.type === 'review' && areThereAnyVideos.value) {
      trackingIndex = 2
    } else if (image.type === 'review' && !areThereAnyVideos.value) {
      trackingIndex = 1
    } else if (
      !image.isExtra &&
      areThereAnyVideos.value &&
      areThereAnyReviews.value
    ) {
      // if it's after a video AND a review, we need to add 2 to the index
      const indexAfterVideoAndReview =
        extraItemsIndexes.value[extraItemsIndexes.value.length - 1] + 1
      trackingIndex =
        indexInList < indexAfterVideoAndReview ? indexInList + 2 : indexInList
    } else if (
      !image.isExtra &&
      ((areThereAnyVideos.value && !areThereAnyReviews.value) ||
        (!areThereAnyVideos.value && areThereAnyReviews.value))
    ) {
      // if it's after a video OR a review, we need to add 1 to the index
      const indexAfterVideoAndReview =
        extraItemsIndexes.value[extraItemsIndexes.value.length - 1] + 1
      trackingIndex =
        indexInList < indexAfterVideoAndReview ? indexInList + 1 : indexInList
    } else if (
      !image.isExtra &&
      !areThereAnyVideos.value &&
      !areThereAnyReviews.value
    ) {
      trackingIndex = indexInList
    }
  }

  return trackingIndex
}

// only tracks each image impression once (via useEmitOnce + IntersectionObserver)
function onVisibleImage(index: number, imageType?: 'review' | 'video') {
  const image = carouselImages.value[index]
  const trackingIndex = getTrackingIndex(image, index)

  // Skip tracking for images visible without having scrolled in mobile
  if (isMobile.value && (trackingIndex === 1 || trackingIndex === 2)) {
    return
  }

  trackCarouselImpression({
    name: 'product',
    zone: 'pp_carousel',
    currentSlide: trackingIndex,
    value: `${trackingIndex + 1}/${carouselImages.value.length}`,
    imageType:
      // eslint-disable-next-line no-nested-ternary
      imageType === 'review'
        ? 'review_image'
        : imageType === 'video'
          ? 'vr_video'
          : 'product_image',
  })
}

function onScrollStart(): void {
  trackClick({
    name: 'scroll_start',
    zone: 'pp_content_carousel',
    product_model: trackingProductModel ?? '',
  })
}

let scrollEndTimeout: ReturnType<typeof setTimeout> | null = null
function onScrollEnd(): void {
  if (scrollEndTimeout) {
    clearTimeout(scrollEndTimeout)
  }
  // This should be triggered after all the images have been loaded
  // and all 'carousel_impression' events have been emitted, so
  // we can trigger the 'scroll_end' event after a small delay.
  scrollEndTimeout = setTimeout(
    () => {
      trackClick({
        name: 'scroll_end',
        zone: 'pp_content_carousel',
        product_model: trackingProductModel ?? '',
      })
    },
    (carouselImages.value.length + 1) * 10,
  )
}

const isMediaViewerOpen = shallowRef(false)
const currentActiveIndex = shallowRef(0)
const mediaViewerIndex = shallowRef(currentActiveIndex.value)

/**
 * This function should only be called when the main image is clicked.
 * And when the image is not an extra item.
 */
function openMediaViewer(image: GalleryImage): void {
  if (allowMediaViewer) {
    isMediaViewerOpen.value = true

    // Media Viewer modal only has `baseCarouselImages`
    const indexFromBaseCarouselImages = baseCarouselImages.value.findIndex(
      (i) => i.src === image.src,
    )
    mediaViewerIndex.value = indexFromBaseCarouselImages

    trackClick({
      name: 'photo_carousel',
      zone: 'pp_content_carousel',
      product_model: trackingProductModel ?? '',
      currentSlide: indexFromBaseCarouselImages,
    })

    openModal(MODAL_NAMES.MEDIA_VIEWER)
  }
}

function onMediaViewerClose(): void {
  isMediaViewerOpen.value = false
  mediaViewerIndex.value = 0
}

// Extra items handling
// ================================================

function trackExtraItemClick(type: 'review' | 'video'): void {
  if (type === 'review' && currentReview.value) {
    trackClick({
      name: 'go_to_reviews',
      page_type: 'product',
      value: {
        average_rate: currentReview.value.averageRate,
        count: currentReview.value.count,
      },
      zone: 'image_carousel',
    })
  }
}

function onEnterAltImage(index: number): void {
  if (!isMobile.value) {
    currentActiveIndex.value = index
  }
}

function handleAltImageItemClick(
  index: number,
  imageType?: 'review' | 'video',
): void {
  if (!imageType && isMobile.value) {
    openMediaViewer(carouselImages.value[index])
  }

  if (imageType) {
    trackExtraItemClick(imageType)

    if (isMobile.value) {
      if (imageType === 'review' && currentReview.value) {
        const reviewCardRef = mainImageContainer.value?.reviewCard
        if (reviewCardRef) {
          const reviewCard = Array.isArray(reviewCardRef)
            ? reviewCardRef[0]
            : reviewCardRef
          if (reviewCard && typeof reviewCard.openModal === 'function') {
            reviewCard.openModal()
          }
        }
      }

      if (imageType === 'video') {
        const videoCardRef = mainImageContainer.value?.videoCard
        if (videoCardRef) {
          const videoCard = Array.isArray(videoCardRef)
            ? videoCardRef[0]
            : videoCardRef
          if (videoCard && typeof videoCard.playVideo === 'function') {
            videoCard.playVideo()
          }
        }
      }
    }
  }
}

function getAltImageEvents(
  image:
    | {
        src: string
        alt: string
      }
    | {
        src: string
        alt: string
        isExtra: true
        type: 'review' | 'video'
      },
  index: number,
): Record<string, unknown> {
  return {
    onVisible: () => onVisibleImage(index),
    onEnter: () => onEnterAltImage(index),
    onClick: () =>
      handleAltImageItemClick(
        index,
        'isExtra' in image ? image.type : undefined,
      ),
  }
}

// Reset the active index when the window is resized to mobile
// This way, the main image is the first one (like when you first load the page)
watch(isMobile, (isMobileNow, wasMobile) => {
  if (isMobileNow && !wasMobile) {
    currentActiveIndex.value = 0
    mediaViewerIndex.value = 0
  }
})

onUnmounted(() => {
  if (scrollEndTimeout) {
    clearTimeout(scrollEndTimeout)
  }
})
</script>
